import React, { useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  TagsWrapper,
  FilterTagWrapper,
  ResetFiltersCTA,
  ResetLabel,
  FilterTag,
  Label,
  IconBox,
} from './FilterTags.style';
import { clearAllFilters, selectFilters, removeFilter } from '../../reducers/filtersReducer';
import useFilterTagProcessor from '../../hooks/useFilterTagProcessor';
import useFilterOptions from '../../hooks/useFilterOptions';
import CloseIcon from '../../assets/icons/CloseIcon';
import {
  filterClearIndividual,
  filterClearAll,
  filterCurrentState,
} from '../../utils/analyticsEventListeners';
import { INTIAL_FILTERS } from '../../constants/initialFilters';
import { selectZipCode } from '../../reducers/zipCodeReducer';

const FilterTags: React.FC = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { listOfAllFilters } = useFilterOptions();
  const { confirmedFilters } = useSelector(selectFilters);

  const { market } = useSelector(selectZipCode);

  const isCaMarket = market === 'CA';

  const { renderStringFilters, filterCount } = useFilterTagProcessor(
    confirmedFilters,
    listOfAllFilters,
    isCaMarket,
  );

  const resetAllFilters = useCallback(() => {
    filterClearAll(confirmedFilters);
    dispatch(clearAllFilters());
    filterCurrentState(INTIAL_FILTERS);
  }, [confirmedFilters, dispatch]);

  const handleRemoveFilter = useCallback(
    (filterType: string, filter: string) => {
      filterClearIndividual(confirmedFilters, `${filterType}.${filter}`);
      dispatch(removeFilter({ filterType, filterValue: filter }));
    },
    [confirmedFilters, dispatch],
  );

  const resetFiltersComponent = useMemo(() => {
    return (
      <ResetFiltersCTA data-testid="reset-all-filters">
        <ResetLabel onClick={resetAllFilters} data-testid="reset-all-filters-button">
          {t('promo.filterTags.resetAll')}
        </ResetLabel>
      </ResetFiltersCTA>
    );
  }, [resetAllFilters, t]);

  const regionFiltersSelected = renderStringFilters.filter(
    (toCheck) => toCheck.filterType === 'offerRegion',
  );
  const isOnlyNatSelected =
    regionFiltersSelected.length === 1 &&
    regionFiltersSelected.some((toCheck) => toCheck.value === 'NAT');

  const shouldShowRemoveAllFilters =
    (isCaMarket && renderStringFilters.length > 1) ||
    (isCaMarket && renderStringFilters.length === 1 && !isOnlyNatSelected) ||
    (!isCaMarket && !!filterCount);

  const renderVehicleTypeTag = useMemo(() => {
    if (!isCaMarket) return null;
    return (
      <FilterTag data-testid="vehicle-type-tag">
        <Label data-testid="vehicle-type-label">
          {t(`promo.filters.filterOptions.vehicleType.${confirmedFilters.vehicleType}`)}
        </Label>
      </FilterTag>
    );
  }, [confirmedFilters.vehicleType, isCaMarket, t]);

  const renderContent = useMemo(() => {
    if (!filterCount) return <></>;
    return (
      <TagsWrapper data-testid="filter-tags-wrapper">
        <FilterTagWrapper data-testid="filter-tags-list">
          {renderVehicleTypeTag}
          {renderStringFilters.map((filter) => {
            const { label, filterType, value, showType, preLabel } = filter;
            const shouldRemoveCloseIcon =
              confirmedFilters.offerRegion.length === 1 && value === 'NAT';
            return (
              <FilterTag
                key={`${filterType}+${label}+${value}`}
                data-testid={`filter-tag-${filterType}-${value}`}
              >
                <Label data-testid={`filter-tag-label-${filterType}-${value}`}>
                  {showType && `${preLabel}:`} {label}
                </Label>
                {!shouldRemoveCloseIcon && (
                  <IconBox
                    onClick={() => handleRemoveFilter(filterType, value)}
                    data-testid={`remove-filter-${filterType}-${value}`}
                  >
                    <CloseIcon size={12} />
                  </IconBox>
                )}
              </FilterTag>
            );
          })}
          {shouldShowRemoveAllFilters && resetFiltersComponent}
        </FilterTagWrapper>
      </TagsWrapper>
    );
  }, [
    confirmedFilters.offerRegion.length,
    filterCount,
    handleRemoveFilter,
    renderStringFilters,
    renderVehicleTypeTag,
    resetFiltersComponent,
    shouldShowRemoveAllFilters,
  ]);

  return renderContent;
};

export default FilterTags;
